import { useAuth0 } from '@auth0/auth0-react';
import { Link } from '@tanstack/react-router';
import {
  LogOut,
  Menu,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Sun,
  User,
} from 'lucide-react';
import { useEffect, useState } from 'react';

import Sidebar from '@/components/Sidebar';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  applyTheme,
  getPreferredTheme,
  persistTheme,
  type ThemeMode,
} from '@/lib/theme';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const { user } = useAuth0();
  const [theme, setTheme] = useState<ThemeMode>(() => getPreferredTheme());
  const [isNavigationVisible, setIsNavigationVisible] = useState(true);
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);

  useEffect(() => {
    applyTheme(theme);
    persistTheme(theme);
  }, [theme]);

  return (
    <div className="flex h-dvh overflow-hidden bg-background">
      {isNavigationVisible && (
        <Sidebar id="admin-navigation" className="hidden md:block" />
      )}

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="border-b bg-card px-3 py-3 sm:px-6 sm:py-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2 sm:gap-3">
              <Sheet
                open={mobileNavigationOpen}
                onOpenChange={setMobileNavigationOpen}
              >
                <SheetTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="shrink-0 md:hidden"
                    aria-label="Open navigation"
                  >
                    <Menu className="h-4 w-4" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[min(20rem,85vw)] p-0">
                  <SheetHeader className="sr-only">
                    <SheetTitle>Navigation</SheetTitle>
                  </SheetHeader>
                  <Sidebar
                    id="admin-mobile-navigation"
                    className="w-full border-r-0"
                    onNavigate={() => setMobileNavigationOpen(false)}
                  />
                </SheetContent>
              </Sheet>
              <Button
                variant="outline"
                size="icon"
                className="hidden shrink-0 md:inline-flex"
                onClick={() => setIsNavigationVisible((visible) => !visible)}
                aria-controls="admin-navigation"
                aria-expanded={isNavigationVisible}
                aria-label={
                  isNavigationVisible ? 'Hide navigation' : 'Show navigation'
                }
                title={
                  isNavigationVisible ? 'Hide navigation' : 'Show navigation'
                }
              >
                {isNavigationVisible ? (
                  <PanelLeftClose className="h-4 w-4" />
                ) : (
                  <PanelLeftOpen className="h-4 w-4" />
                )}
              </Button>
              <h1 className="truncate text-lg font-semibold sm:text-2xl">
                Admin Dashboard
              </h1>
              {import.meta.env.DEV && (
                <span className="hidden rounded bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-600 sm:inline dark:text-amber-400">
                  Admin gate bypassed in dev
                </span>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2 sm:gap-4">
              <div className="hidden items-center gap-2 text-sm text-muted-foreground xl:flex">
                <User className="h-4 w-4" />
                <span className="max-w-64 truncate">{user?.email}</span>
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={() =>
                  setTheme((mode) => (mode === 'dark' ? 'light' : 'dark'))
                }
                aria-label="Toggle color theme"
              >
                {theme === 'dark' ? (
                  <Sun className="h-4 w-4" />
                ) : (
                  <Moon className="h-4 w-4" />
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="px-2 sm:px-3"
                asChild
              >
                <Link to="/logout" aria-label="Sign out">
                  <LogOut className="h-4 w-4" />
                  <span className="hidden sm:inline">Sign Out</span>
                </Link>
              </Button>
            </div>
          </div>
        </header>

        <main className="min-w-0 flex-1 overflow-x-hidden overflow-y-auto p-3 sm:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
